/**
 * Shared WebSocket singleton for the whole SPA.
 * Open the connection from layout (e.g. Header) so every route receives broadcasts.
 * Other screens only call connectWs() to reuse the same socket or connect if needed.
 */
let ws = null;
let connectingPromise = null;
const listeners = new Set();
let lastMessageAt = 0;
let lastPayload = null;
let lastConnectedUrl = null;
let recvCount = 0;

// FIX 1: Heartbeat/ping-pong to keep connection alive and detect stale sockets.
// AWS API Gateway WebSocket closes idle connections after 10 minutes.
// Without this, the socket appears open (readyState=1) but is actually dead.
let heartbeatInterval = null;
const HEARTBEAT_INTERVAL_MS = 4 * 60 * 1000; // ping every 4 minutes

function startHeartbeat() {
  stopHeartbeat();
  heartbeatInterval = setInterval(() => {
    if (isWsOpen()) {
      sendWs({ action: "ping" });
    } else {
      stopHeartbeat();
    }
  }, HEARTBEAT_INTERVAL_MS);
}

function stopHeartbeat() {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
}

/** localStorage sometimes stores ids with accidental quotes — breaks $connect query parsing */
export function normalizeWsId(value) {
  if (value == null || value === "") return "";
  return String(value).trim().replace(/^['"]+|['"]+$/g, "");
}

/** Persisted when user selects a chat in Communication; Header reads this to join_chat on any route */
export const WS_ACTIVE_CHAT_ID_KEY = "wsActiveChatId";

function uniq(arr) {
  return Array.from(new Set(arr.filter(Boolean)));
}

function normalizeBaseUrl(baseUrl) {
  return String(baseUrl || "").replace(/\/$/, "");
}

function buildUrlCandidates(baseUrl) {
  const base = normalizeBaseUrl(baseUrl);
  return uniq([
    base,
    `${base}/`,
    base.endsWith("/production") ? base.replace(/\/production$/i, "/Production") : null,
    base.endsWith("/production") ? `${base.replace(/\/production$/i, "/Production")}/` : null,
    base.endsWith("/Production") ? base.replace(/\/Production$/i, "/production") : null,
    base.endsWith("/Production") ? `${base.replace(/\/Production$/i, "/production")}/` : null
  ]);
}

function buildQueryVariants({ userId, userType }) {
  const uid = normalizeWsId(userId);
  const raw = String(userType || "user");
  const upper = raw.toUpperCase();
  const lower = raw.toLowerCase();
  const mappedUpper = upper === "SUPERADMIN" ? "ADMIN" : upper;

  const variants = [
    { userId: uid, userType: mappedUpper },
    { userId: uid, userType: upper },
    { userid: uid, userType: mappedUpper },
    { id: uid, userType: mappedUpper },
    { userId: uid, userType: lower },
    { userId: uid, userType: raw }
  ];

  const seen = new Set();
  return variants.filter((v) => {
    const qs = new URLSearchParams(v).toString();
    if (seen.has(qs)) return false;
    seen.add(qs);
    return true;
  });
}

function notify(data) {
  recvCount += 1;
  lastMessageAt = Date.now();
  lastPayload = data;
  listeners.forEach((fn) => {
    try {
      fn(data);
    } catch (_) {
      // ignore listener errors — one bad listener must never block others
    }
  });
}

/**
 * Many backends wrap chat events (Lambda proxy, HTTP-style responses, nested `data`).
 * Unwrap so Communication/Header see a flat `{ type, action, chatId, message }`.
 */
export function normalizeInboundPayload(raw) {
  if (raw == null || typeof raw !== "object") return raw;

  let p = raw;

  // Unwrap stringified JSON in `body` (Lambda/API Gateway patterns)
  for (let i = 0; i < 2; i++) {
    if (typeof p.body !== "string") break;
    const trimmed = p.body.trim();
    if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) break;
    try {
      const inner = JSON.parse(p.body);
      if (inner && typeof inner === "object") {
        p = { ...inner, _wsEnvelope: p };
      }
    } catch {
      break;
    }
  }

  const mergeNested = (obj, key) => {
    const n = obj[key];
    if (!n || typeof n !== "object") return obj;
    if (n.message || n.chatId || n.type || n.action || n.msg || n.content) {
      return { ...obj, ...n };
    }
    return obj;
  };

  p = mergeNested(p, "data");
  p = mergeNested(p, "payload");
  p = mergeNested(p, "result");
  return p;
}

function attachWsHandlers(currentWs) {
  currentWs.onmessage = (event) => {
    console.log("[ws] recv raw", event.data);
    try {
      const raw = JSON.parse(event.data);
      const payload = normalizeInboundPayload(raw);
      console.log("[ws] recv parsed", payload, {
        listenerCount: listeners.size,
        recvCount: recvCount + 1
      });
      notify(payload);
    } catch (e) {
      const preview =
        typeof event.data === "string"
          ? event.data.slice(0, 500)
          : String(event.data);
      console.warn("[ws] recv JSON parse error:", e?.message || e, preview);
    }
  };

  currentWs.onerror = () => {
    // browser hides details; consumers will see disconnect via onclose
  };

  // FIX 2: Stop heartbeat when socket closes so interval doesn't run forever.
  currentWs.onclose = () => {
    stopHeartbeat();
    if (ws === currentWs) {
      ws = null;
      connectingPromise = null;
      lastConnectedUrl = null;
    }
  };
}

function connectOnce(url, queryVariants, timeoutMs = 5000) {
  return new Promise((resolve, reject) => {
    let idx = 0;
    let settled = false;
    let current = null;

    const tryNext = () => {
      if (settled) return;
      if (idx >= queryVariants.length) {
        reject(new Error("WebSocket error"));
        return;
      }

      const qs = new URLSearchParams(queryVariants[idx++]).toString();
      const attemptUrl = `${url}?${qs}`;
      if (localStorage.getItem("wsDebug") === "1") {
        // eslint-disable-next-line no-console
        console.log("[ws] connecting", attemptUrl);
      }
      current = new WebSocket(attemptUrl);

      const timer = setTimeout(() => {
        try { current.close(); } catch (_) {}
        tryNext();
      }, timeoutMs);

      current.onopen = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(current);
      };

      current.onerror = () => {
        clearTimeout(timer);
        try { current.close(); } catch (_) {}
        tryNext();
      };

      current.onclose = () => {
        clearTimeout(timer);
        if (!settled) tryNext();
      };
    };

    tryNext();
  });
}

export function addWsListener(fn) {
  listeners.add(fn);
  console.log("[ws] listener registered", { listenerCount: listeners.size });
  return () => {
    listeners.delete(fn);
    console.log("[ws] listener removed", { listenerCount: listeners.size });
  };
}

export function isWsOpen() {
  return !!ws && ws.readyState === WebSocket.OPEN;
}

export function sendWs(message) {
  if (!isWsOpen()) {
    console.log("[ws] send skipped, socket not open", {
      readyState: ws?.readyState ?? null,
      message
    });
    return false;
  }
  try {
    const body = JSON.stringify(message);
    console.log("[ws] send", message, {
      url: ws?.url ?? null,
      readyState: ws.readyState,
      body
    });
    ws.send(body);
    return true;
  } catch (error) {
    console.log("[ws] send failed", error?.message || error, message);
    return false;
  }
}

export function getWsDebugSnapshot() {
  return {
    open: isWsOpen(),
    readyState: ws?.readyState ?? null,
    url: ws?.url ?? null,
    lastConnectedUrl,
    recvCount,
    lastMessageAt,
    lastPayload,
    listenerCount: listeners.size,    // FIX 3: expose listener count for debug
    heartbeatActive: heartbeatInterval !== null
  };
}

export function closeWs() {
  stopHeartbeat();
  if (ws) {
    try { ws.close(); } catch (_) {}
  }
  ws = null;
  connectingPromise = null;
}

export async function connectWs({ baseUrl, userId, userType }) {
  const uid = normalizeWsId(userId);
  const ut = normalizeWsId(userType) || "user";
  if (!baseUrl || !uid) throw new Error("baseUrl and userId required");

  // Reuse existing open or connecting socket
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
    return ws;
  }
  if (connectingPromise) return connectingPromise;

  const candidates = buildUrlCandidates(baseUrl);
  const queryVariants = buildQueryVariants({ userId: uid, userType: ut });

  connectingPromise = (async () => {
    let lastErr = null;
    for (const url of candidates) {
      try {
        // eslint-disable-next-line no-console
        console.log("WebSocket connecting to:", url, { userId: uid, userType: ut });
        const opened = await connectOnce(url, queryVariants);
        ws = opened;
        lastConnectedUrl = opened?.url || null;
        attachWsHandlers(opened);

        // Send initial ping so backend maps connectionId → userId in WsConnection table.
        // This is critical — without it the receiver's connectionId is never stored
        // and broadcastNewMessageToChatParticipants cannot find them.
        sendWs({
          action: "ping",
          userId: uid,
          userid: uid,
          id: uid,
          userType: ut
        });

        // FIX 1: Start heartbeat after connect to prevent AWS idle timeout (10 min).
        startHeartbeat();

        // eslint-disable-next-line no-console
        console.log("WebSocket connected:", opened?.url || "(no url)");
        return ws;
      } catch (e) {
        lastErr = e;
        // eslint-disable-next-line no-console
        console.warn("WebSocket connect failed:", url, e?.message || e);
      }
    }
    ws = null;
    connectingPromise = null;
    throw lastErr || new Error("WebSocket connection failed");
  })();

  return connectingPromise;
}